import React, {useState} from 'react';
import {Linking, StyleSheet, TouchableOpacity, View} from 'react-native';
import FontAwesome5 from 'react-native-vector-icons/FontAwesome5';
import CCText from '@/Atoms/CCText';
import Box from '@/Atoms/Box';
import {FormatRating, LinkedAccounts} from '@/Hooks/PlayerProfileApi/types';
import ThunderText from '../ui/ThunderText';
import FormatIcon from '../ui/FormatIcon';

// Per-platform display metadata (mark swatch + accent), matching the design
// handoff v2 "Linked accounts" card: platform pills sit top-right in the header
// as a switcher, and a single 4-up format grid below shows the active platform.
const PLATFORM_META: Record<
  string,
  {name: string; mark: string; markBg: string}
> = {
  lichess: {name: 'Lichess', mark: 'Li', markBg: '#4D3F37'},
  chess_com: {name: 'Chess.com', mark: 'Ch', markBg: '#6B9B3F'},
};

// Where the pill's external-link glyph actually goes -- opens the player's
// real profile on the linked platform (previously the pill had no outbound
// link at all, only the in-app active-platform toggle below).
const PLATFORM_PROFILE_URL: Record<string, (handle: string) => string> = {
  chess_com: handle => `https://www.chess.com/member/${handle}`,
  lichess: handle => `https://lichess.org/@/${handle}`,
};

// The design always renders the four canonical formats in this order, greying
// out any the player has no rating for ("No Data"). We don't ship the design's
// format SVGs, so a colored swatch with the format initial stands in for the
// 44x44 icon block.
const FORMATS: {key: string; label: string; bg: string; fg: string}[] = [
  {key: 'bullet', label: 'Bullet', bg: '#FFF0C8', fg: '#B5652A'},
  {key: 'blitz', label: 'Blitz', bg: '#E9F3FF', fg: '#3A6FA5'},
  {key: 'rapid', label: 'Rapid', bg: '#FAF0D1', fg: '#A97B18'},
  {key: 'classical', label: 'Classical', bg: '#F3EEFF', fg: '#5A4A8A'},
];

const styles = StyleSheet.create({
  // Spec §2 card shell.
  card: {
    backgroundColor: '#fff',
    borderRadius: 20,
    paddingTop: 20,
    paddingBottom: 22,
    paddingHorizontal: 24,
    marginBottom: 18,
    shadowColor: 'rgba(77,63,55,1)',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  // Header: title/subtitle left, platform switcher + link chip pushed right.
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 14,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#262322',
  },
  headerSub: {
    fontSize: 12,
    color: '#888280',
    marginTop: 2,
  },
  switcher: {
    marginLeft: 'auto',
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    // React Native's flex default is flexShrink:0 (unlike web CSS's 1), so
    // without this the switcher never shrinks to the header row's actual
    // available width on a narrow viewport -- it just overflows past the
    // card edge instead of wrapping the platform pill/link chip onto their
    // own line.
    flexShrink: 1,
    minWidth: 0,
  },
  // Platform pill: mark swatch + name + @handle, 1.5px border, fully round.
  platformPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    paddingVertical: 9,
    paddingLeft: 11,
    paddingRight: 16,
    borderRadius: 999,
    borderWidth: 1.5,
  },
  platformMark: {
    width: 22,
    height: 22,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  platformMarkText: {
    fontSize: 11,
    fontWeight: '600',
  },
  platformName: {
    fontSize: 13,
    fontWeight: '600',
    color: '#262322',
  },
  platformHandle: {
    fontSize: 11,
  },
  platformPillContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
  },
  externalLinkBtn: {
    marginLeft: 2,
    padding: 2,
  },
  // Owner-only "Link {platform}" affordance: dashed, cream.
  linkChip: {
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: 'rgba(38,35,34,0.32)',
    borderRadius: 999,
    backgroundColor: '#FAF6EB',
    paddingVertical: 9,
    paddingHorizontal: 15,
  },
  linkChipText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#4D3F37',
  },
  // Single 4-up format grid for the active platform.
  formatsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 14,
    marginTop: 18,
  },
  formatCard: {
    flexGrow: 1,
    flexBasis: '22%',
    minWidth: 150,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    backgroundColor: '#FAF6EB',
    borderWidth: 2,
    borderColor: 'transparent',
    borderRadius: 16,
    paddingVertical: 15,
    paddingHorizontal: 18,
  },
  formatSwatch: {
    width: 44,
    height: 44,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  formatBody: {
    flex: 1,
    minWidth: 0,
  },
  formatName: {
    fontSize: 13,
    fontWeight: '600',
    color: '#262322',
  },
  ratingRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 7,
  },
  formatRating: {
    marginTop: 1,
  },
  trend: {
    fontSize: 12,
    fontWeight: '600',
  },
  formatGames: {
    fontSize: 11,
    color: '#888280',
  },
  formatUnrated: {
    fontSize: 15,
    color: '#A8A7A6',
    marginTop: 3,
  },
  sideBySideWrap: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(38,35,34,0.08)',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 16,
  },
  sideBySideLabel: {
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: 0.84,
    textTransform: 'uppercase',
    color: '#888280',
  },
  sideBySideChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    backgroundColor: '#FAF6EB',
    borderRadius: 11,
    paddingVertical: 8,
    paddingHorizontal: 13,
  },
  sideBySideFormat: {
    fontSize: 12,
    color: '#888280',
  },
  sideBySideValue: {
    fontSize: 13,
    fontWeight: '600',
    color: '#262322',
  },
  sideBySideVs: {
    fontSize: 11,
    color: '#A8A7A6',
  },
  sideBySideGap: {
    fontSize: 11,
    fontWeight: '600',
  },
});

// One platform pill in the header switcher. `active` drives the highlighted
// (selected) look vs. the muted look, exactly like the design's platTabs.
function PlatformPill({
  platformKey,
  handle,
  active,
  onPress,
}: {
  platformKey: string;
  handle: string;
  active: boolean;
  onPress?: () => void;
}) {
  const meta = PLATFORM_META[platformKey] ?? {
    name: platformKey,
    mark: platformKey.slice(0, 2),
    markBg: '#4D3F37',
  };
  const profileUrl = PLATFORM_PROFILE_URL[platformKey]?.(handle);
  const handleColor = active ? '#806234' : '#A8A7A6';
  return (
    <View
      style={[
        styles.platformPill,
        {
          borderColor: active ? '#FACF47' : 'rgba(38,35,34,0.18)',
          backgroundColor: active ? '#FAF0D1' : '#fff',
        },
      ]}>
      <TouchableOpacity
        activeOpacity={onPress ? 0.7 : 1}
        onPress={onPress}
        disabled={!onPress}
        style={styles.platformPillContent}>
        <View
          style={[
            styles.platformMark,
            {backgroundColor: active ? meta.markBg : '#EFE7D2'},
          ]}>
          <CCText
            style={[
              styles.platformMarkText,
              {color: active ? '#fff' : '#888280'},
            ]}>
            {meta.mark}
          </CCText>
        </View>
        <CCText style={styles.platformName}>{meta.name}</CCText>
        <CCText style={[styles.platformHandle, {color: handleColor}]}>
          @{handle}
        </CCText>
      </TouchableOpacity>
      {profileUrl ? (
        <TouchableOpacity
          testID={`open-${platformKey}-profile`}
          accessibilityLabel={`Open ${meta.name} profile in a new tab`}
          onPress={() => Linking.openURL(profileUrl)}
          hitSlop={{top: 8, bottom: 8, left: 6, right: 6}}
          style={styles.externalLinkBtn}>
          <FontAwesome5
            name="external-link-alt"
            size={10}
            color={handleColor}
          />
        </TouchableOpacity>
      ) : null}
    </View>
  );
}

// The backend is mid-rollout from a bare `rating` number per format to the
// richer {rating, games, trend} object. Accept either so the card renders
// correctly whether it's talking to an already-deployed or a not-yet-deployed
// backend; a legacy number just has no games/trend to show.
function normalizeRating(
  value: FormatRating | number | null | undefined,
): FormatRating | null {
  if (value == null) {
    return null;
  }
  if (typeof value === 'number') {
    return {rating: value, games: null, trend: null};
  }
  return typeof value.rating === 'number' ? value : null;
}

// The 4-up format grid for one platform's ratings. Always renders the four
// canonical formats; a missing one reads "No Data".
function FormatGrid({
  formats,
}: {
  formats: Record<string, FormatRating | number>;
}) {
  return (
    <View style={styles.formatsRow}>
      {FORMATS.map(fmt => {
        const data = normalizeRating(formats[fmt.key]);
        return (
          <View key={fmt.key} style={styles.formatCard}>
            <View style={[styles.formatSwatch, {backgroundColor: fmt.bg}]}>
              <FormatIcon format={fmt.key} size={26} />
            </View>
            <View style={styles.formatBody}>
              <CCText style={styles.formatName}>{fmt.label}</CCText>
              {data ? (
                <>
                  <View style={styles.ratingRow}>
                    <ThunderText size={26} style={styles.formatRating}>
                      {data.rating}
                    </ThunderText>
                    {data.trend != null && data.trend !== 0 ? (
                      <CCText
                        style={[
                          styles.trend,
                          {color: data.trend > 0 ? '#2F7F4C' : '#CD2C39'},
                        ]}>
                        {data.trend > 0 ? `+${data.trend}` : data.trend}
                      </CCText>
                    ) : null}
                  </View>
                  {data.games != null ? (
                    <CCText style={styles.formatGames}>
                      {data.games} games
                    </CCText>
                  ) : null}
                </>
              ) : (
                <CCText style={styles.formatUnrated}>No Data</CCText>
              )}
            </View>
          </View>
        );
      })}
    </View>
  );
}

// Read-only per-format comparison, only when both platforms are linked --
// the design's "side by side" strip. Only formats present on *both* platforms
// get a row.
function SideBySide({
  lichess,
  chessCom,
}: {
  lichess: Record<string, FormatRating | number>;
  chessCom: Record<string, FormatRating | number>;
}) {
  const shared = FORMATS.map(f => f.key).filter(
    k => k in lichess && k in chessCom,
  );
  if (!shared.length) {
    return null;
  }
  return (
    <View style={styles.sideBySideWrap}>
      <CCText style={styles.sideBySideLabel}>Side by side</CCText>
      {shared.map(key => {
        const lic = normalizeRating(lichess[key]);
        const cdc = normalizeRating(chessCom[key]);
        if (!lic || !cdc) {
          return null;
        }
        const licRating = lic.rating;
        const cdcRating = cdc.rating;
        const gap = licRating - cdcRating;
        const label = FORMATS.find(f => f.key === key)?.label ?? key;
        return (
          <View key={key} style={styles.sideBySideChip}>
            <FormatIcon format={key} size={18} />
            <CCText style={styles.sideBySideFormat}>{label}</CCText>
            <CCText style={styles.sideBySideValue}>{licRating}</CCText>
            <CCText style={styles.sideBySideVs}>vs</CCText>
            <CCText style={styles.sideBySideValue}>{cdcRating}</CCText>
            <CCText
              style={[
                styles.sideBySideGap,
                {color: gap === 0 ? '#888280' : gap > 0 ? '#4D3F37' : '#2F7F4C'},
              ]}>
              {gap === 0
                ? 'level'
                : gap > 0
                ? `+${gap} Li`
                : `${-gap} Ch`}
            </CCText>
          </View>
        );
      })}
    </View>
  );
}

// "+ Link Lichess" / "+ Link Chess.com" -- previously a plain, unpressable
// View (decorative only). Routes to the same Settings screen that actually
// saves lic_name/cdc_name (see Settings/index.tsx "Your Chess Profile"
// section) -- the "Edit profile" CTA in ProfileHeader uses this identical
// `navigation.navigate('Settings')` call.
function LinkChip({label, navigation}: {label: string; navigation: any}) {
  return (
    <TouchableOpacity
      testID={`link-chip-${label}`}
      accessibilityRole="button"
      onPress={() => navigation?.navigate('Settings')}
      style={styles.linkChip}>
      <CCText style={styles.linkChipText}>{label}</CCText>
    </TouchableOpacity>
  );
}

type Props = {
  linkedAccounts: LinkedAccounts;
  isOwner: boolean;
  navigation?: any;
};

// Three states: `null` (neither linked), only one platform present, or both.
export default function LinkedAccountsRow({
  linkedAccounts,
  isOwner,
  navigation,
}: Props) {
  const lichess = linkedAccounts?.lichess ?? null;
  const chessCom = linkedAccounts?.chess_com ?? null;
  const bothLinked = !!lichess && !!chessCom;

  // Active platform for the format grid; only meaningful when both are linked
  // (otherwise the single linked platform is always active).
  const [activePlat, setActivePlat] = useState<'lichess' | 'chess_com'>(
    lichess ? 'lichess' : 'chess_com',
  );
  const active = bothLinked ? activePlat : lichess ? 'lichess' : 'chess_com';
  const activeFormats =
    active === 'lichess' ? lichess?.formats : chessCom?.formats;

  const neitherLinked = !lichess && !chessCom;

  // A visitor with nothing to see here gets no card at all -- only the owner
  // sees the "+ Link Lichess"/"+ Link Chess.com" empty state.
  if (neitherLinked && !isOwner) {
    return null;
  }

  const subtitle = bothLinked
    ? 'Both accounts linked — ratings shown per platform, games merged in one history'
    : lichess
    ? 'Lichess only'
    : chessCom
    ? 'Chess.com only'
    : 'Ratings shown per platform';

  return (
    <Box style={styles.card}>
      <View style={styles.header}>
        <View>
          <CCText style={styles.headerTitle}>Linked accounts</CCText>
          <CCText style={styles.headerSub}>{subtitle}</CCText>
        </View>

        {!neitherLinked ? (
          <View style={styles.switcher}>
            {lichess ? (
              <PlatformPill
                platformKey="lichess"
                handle={lichess.username ?? 'lichess'}
                active={active === 'lichess'}
                onPress={bothLinked ? () => setActivePlat('lichess') : undefined}
              />
            ) : isOwner ? (
              <LinkChip label="+ Link Lichess" navigation={navigation} />
            ) : null}

            {chessCom ? (
              <PlatformPill
                platformKey="chess_com"
                handle={chessCom.username ?? 'chess_com'}
                active={active === 'chess_com'}
                onPress={
                  bothLinked ? () => setActivePlat('chess_com') : undefined
                }
              />
            ) : isOwner ? (
              <LinkChip label="+ Link Chess.com" navigation={navigation} />
            ) : null}
          </View>
        ) : null}
      </View>

      {neitherLinked ? (
        <View style={[styles.switcher, {marginTop: 16}]}>
          <LinkChip label="+ Link Lichess" navigation={navigation} />
          <LinkChip label="+ Link Chess.com" navigation={navigation} />
        </View>
      ) : (
        <>
          <FormatGrid formats={activeFormats ?? {}} />
          {bothLinked ? (
            <SideBySide
              lichess={lichess.formats}
              chessCom={chessCom.formats}
            />
          ) : null}
        </>
      )}
    </Box>
  );
}
